import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LuChevronDown, LuMenu, LuPhone, LuX } from "react-icons/lu";

import logo from "../assets/mega_electric_logo.png";
import { business, services } from "../content/site";

/**
 * Header — sticky site navigation.
 *
 * Patterns used here:
 *  - Skip link is the first focusable element and targets #main.
 *  - Services is a DISCLOSURE (button + aria-expanded + aria-controls + a list
 *    of links), not an ARIA menu. A menu role would promise arrow-key roving
 *    focus; a disclosure is fully operable with Tab alone and is the correct
 *    pattern for a list of navigation links.
 *  - Every service entry points at the #services section anchor. The
 *    per-service routes do not exist yet, so linking to them would ship dead
 *    links.
 *  - The desktop bar is 80px tall, which matches the global
 *    `scroll-padding-top: 96px` in index.css, so a jumped-to heading is never
 *    covered by the header (WCAG 2.4.11).
 */

/** Section anchors owned by the homepage. Kept in one place so the desktop bar
 *  and the mobile panel can never drift apart. */
const SECTION_LINKS = [
  { href: "/#why-us", label: "Why us" },
  { href: "/#process", label: "Process" },
  { href: "/#service-areas", label: "Service areas" },
  { href: "/#faq", label: "FAQ" },
  { href: "/#contact", label: "Contact" },
];

const SERVICES_ANCHOR = "/#services";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const headerRef = useRef(null);
  const servicesButtonRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const mobileCloseRef = useRef(null);

  const servicesPanelId = useId();
  const mobilePanelId = useId();

  /* --- Elevate the header once the page has moved. Passive + cleaned up. --- */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* --- Escape closes whichever layer is open, focus returns to its trigger. --- */
  useEffect(() => {
    if (!servicesOpen && !mobileOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (servicesOpen) {
        setServicesOpen(false);
        servicesButtonRef.current?.focus();
      } else if (mobileOpen) {
        setMobileOpen(false);
        mobileButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [servicesOpen, mobileOpen]);

  /* --- A pointer press anywhere outside the header closes both layers. --- */
  useEffect(() => {
    if (!servicesOpen && !mobileOpen) return undefined;

    const onPointerDown = (event) => {
      if (headerRef.current?.contains(event.target)) return;
      setServicesOpen(false);
      setMobileOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [servicesOpen, mobileOpen]);

  /* --- Lock body scroll while the mobile panel is open. --- */
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  /* --- Move focus into the mobile panel when it opens. --- */
  useEffect(() => {
    if (mobileOpen) mobileCloseRef.current?.focus();
  }, [mobileOpen]);

  const closeAll = useCallback(() => {
    setServicesOpen(false);
    setMobileOpen(false);
  }, []);

  const desktopLinkClass =
    "inline-flex items-center min-h-tap rounded-md px-2.5 xl:px-3 " +
    "text-body-sm xl:text-body font-heading font-semibold text-ink " +
    "transition-colors duration-base ease-standard " +
    "hover:bg-surface-sunken hover:text-ink-brand active:bg-surface-sunken";

  const mobileLinkClass =
    "flex items-center min-h-tap rounded-md px-3 py-2 " +
    "text-body font-heading font-semibold text-ink " +
    "transition-colors duration-base ease-standard " +
    "hover:bg-surface-sunken hover:text-ink-brand active:bg-surface-sunken";

  return (
    <header
      ref={headerRef}
      className={
        "sticky top-0 z-40 w-full bg-surface border-b border-line " +
        "transition-shadow duration-base ease-standard " +
        (scrolled ? "shadow-md" : "shadow-none")
      }
    >
      <a className="skip-link" href="#main">
        Skip to main content
      </a>

      <div className="container-page flex h-16 lg:h-20 items-center justify-between gap-3">
        {/* Brand */}
        <Link
          to="/"
          onClick={closeAll}
          className="flex min-h-tap shrink-0 items-center rounded-md transition-opacity duration-base ease-standard hover:opacity-80"
        >
          <img
            src={logo}
            width="946"
            height="532"
            alt={`${business.legalName} home`}
            className="h-10 lg:h-12 w-auto"
          />
        </Link>

        {/* ---------------- Desktop navigation ---------------- */}
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-1">
          {/* Services disclosure */}
          <div className="relative">
            <button
              type="button"
              ref={servicesButtonRef}
              onClick={() => setServicesOpen((value) => !value)}
              aria-expanded={servicesOpen}
              aria-controls={servicesPanelId}
              className={desktopLinkClass + " gap-1"}
            >
              Services
              <LuChevronDown
                aria-hidden="true"
                className={
                  "h-4 w-4 transition-transform duration-base ease-standard " +
                  (servicesOpen ? "rotate-180" : "rotate-0")
                }
              />
            </button>

            {servicesOpen && (
              <ul
                id={servicesPanelId}
                aria-label="Services"
                className="absolute right-0 top-full mt-2 w-[min(20rem,calc(100vw-2*var(--gutter)))] list-none rounded-md border border-line bg-surface p-2 shadow-lg"
              >
                {services.map((service) => (
                  <li key={service.slug}>
                    <a
                      href={SERVICES_ANCHOR}
                      onClick={closeAll}
                      className="flex min-h-tap items-center rounded-md px-3 py-2 text-body-sm text-ink transition-colors duration-base ease-standard hover:bg-surface-sunken hover:text-ink-brand active:bg-surface-sunken"
                    >
                      <span className="[overflow-wrap:anywhere]">
                        {service.title}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {SECTION_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={closeAll}
              className={desktopLinkClass}
            >
              {link.label}
            </a>
          ))}

          <a
            href={business.phone.href}
            className="ml-2 hidden xl:inline-flex min-h-tap items-center gap-2 rounded-md px-3 text-body-lg font-heading font-bold tabular text-ink-brand transition-colors duration-base ease-standard hover:bg-surface-brand-soft active:bg-surface-brand-soft"
          >
            <LuPhone aria-hidden="true" className="h-5 w-5" />
            <span>{business.phone.display}</span>
          </a>

          <a
            href={business.phone.href}
            className="btn-urgent ml-2 whitespace-nowrap"
            aria-label={`Call ${business.shortName} now at ${business.phone.display}`}
          >
            <LuPhone aria-hidden="true" className="h-5 w-5" />
            <span>Call Now</span>
          </a>
        </nav>

        {/* ---------------- Mobile controls ---------------- */}
        <div className="flex items-center gap-1 lg:hidden">
          <a
            href={business.phone.href}
            className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-md text-ink-brand transition-colors duration-base ease-standard hover:bg-surface-brand-soft active:bg-surface-brand-soft"
            aria-label={`Call ${business.shortName} at ${business.phone.display}`}
          >
            <LuPhone aria-hidden="true" className="h-6 w-6" />
          </a>

          <button
            type="button"
            ref={mobileButtonRef}
            onClick={() => {
              setServicesOpen(false);
              setMobileOpen((value) => !value);
            }}
            aria-expanded={mobileOpen}
            aria-controls={mobilePanelId}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-md text-ink transition-colors duration-base ease-standard hover:bg-surface-sunken active:bg-surface-sunken"
          >
            <LuMenu aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* ---------------- Mobile panel ---------------- */}
      {mobileOpen && (
        <div
          id={mobilePanelId}
          className="lg:hidden absolute inset-x-0 top-full max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-b border-line bg-surface shadow-lg"
        >
          <nav aria-label="Mobile" className="container-page py-4">
            <div className="flex items-center justify-between gap-3 pb-2">
              <span className="kicker">Menu</span>
              <button
                type="button"
                ref={mobileCloseRef}
                onClick={() => {
                  setMobileOpen(false);
                  mobileButtonRef.current?.focus();
                }}
                aria-label="Close menu"
                className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-md text-ink transition-colors duration-base ease-standard hover:bg-surface-sunken active:bg-surface-sunken"
              >
                <LuX aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>

            <a
              href={business.phone.href}
              onClick={closeAll}
              className="btn-urgent w-full"
              aria-label={`Call ${business.shortName} now at ${business.phone.display}`}
            >
              <LuPhone aria-hidden="true" className="h-5 w-5" />
              <span className="tabular">{business.phone.display}</span>
            </a>

            <ul className="mt-4 list-none space-y-1 p-0">
              <li>
                <a
                  href={SERVICES_ANCHOR}
                  onClick={closeAll}
                  className={mobileLinkClass}
                >
                  Services
                </a>
              </li>
              {SECTION_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={closeAll}
                    className={mobileLinkClass}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>

            <p className="kicker mt-6 mb-2">All services</p>
            <ul className="list-none space-y-1 p-0 pb-2">
              {services.map((service) => (
                <li key={service.slug}>
                  <a
                    href={SERVICES_ANCHOR}
                    onClick={closeAll}
                    className="flex min-h-tap items-center rounded-md px-3 py-2 text-body-sm text-ink-muted transition-colors duration-base ease-standard hover:bg-surface-sunken hover:text-ink-brand active:bg-surface-sunken"
                  >
                    <span className="[overflow-wrap:anywhere]">
                      {service.title}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
